import React from 'react';
import { Link } from 'react-router-dom';

import { locale, getMaxAudioUploads, getPlanTypeLabel } from '@trqk/utils';

import { AudioUploadsEmptyState, AudioUploadProgressRow } from './components';
import {
    modalFooterShimHeight,
    modalFooterHeight,
    tableHeight,
    headerHeight,
} from '../../constants';
import { HASHES, PATHS } from '../../../../../constants';

interface AudioUploadsProps {
    /**
     * Current count of audio files that have been uploaded or are currently being uploaded
     */
    currentAudioUploads: number;
    /**
     * The current {@link DetectionStats} object provided by the useDetectionTracks() hook
     */
    detectionStats: DetectionStats;
    /**
     * A filtered list of `uploadTasks` provided by the useUploadAudioFiles() hook. These tasks are
     * currently in progress; i.e. not complete or stopped due to an error.
     */
    inProgressTasks: UploadAudioTask[];
}

/**
 * Renders a progress bar for each currently uploading audio file.
 * @param props - see {@link AudioUploadsProps}
 */
export const AudioUploads = ({
    currentAudioUploads,
    detectionStats,
    inProgressTasks,
}: AudioUploadsProps): JSX.Element => {
    const maxAudioUploads = getMaxAudioUploads(
        detectionStats.detectionCapacity,
    );
    const uploadsRemaining = maxAudioUploads - currentAudioUploads;
    const planTypeLabel = getPlanTypeLabel(detectionStats.planType);

    const fileOrFiles =
        uploadsRemaining === 1
            ? locale.enUS.MANAGE_DETECTIONS.UPLOADS.AVAILABLE.FILE
            : locale.enUS.MANAGE_DETECTIONS.UPLOADS.AVAILABLE.FILES;

    const uploadsAvailableLabel = `${
        locale.enUS.MANAGE_DETECTIONS.UPLOADS.AVAILABLE.YOUR
    } ${planTypeLabel} ${
        locale.enUS.MANAGE_DETECTIONS.UPLOADS.AVAILABLE.ALLOWS
    } ${uploadsRemaining}
        ${
            locale.enUS.MANAGE_DETECTIONS.UPLOADS.AVAILABLE.MORE_AUDIO
        } ${fileOrFiles} ${
        locale.enUS.MANAGE_DETECTIONS.UPLOADS.AVAILABLE.TO_BE_UPLOADED
    } ${detectionStats.detectionCapacity ?? 0} ${
        locale.enUS.MANAGE_DETECTIONS.UPLOADS.AVAILABLE.OF_YOUR_FILES
    }.`;

    return (
        <>
            <div
                className="bg-white rounded-b-lg relative"
                style={{
                    height: tableHeight + headerHeight + modalFooterShimHeight,
                }}
            >
                <div className="flex flex-col justify-start items-center h-full">
                    <div className="overflow-y-audio overflow-x-hidden respond-to-mouse">
                        {inProgressTasks.map(
                            (task: UploadAudioTask): JSX.Element => (
                                <AudioUploadProgressRow
                                    task={task}
                                    key={task.taskId}
                                />
                            ),
                        )}
                    </div>
                    {inProgressTasks.length === 0 && <AudioUploadsEmptyState />}
                </div>
            </div>
            <div
                className="relative flex items-start justify-end px-10"
                style={{
                    height: modalFooterHeight,
                    // Kind of hacky to declare this fixed padding, but it allows the layout to
                    // remain consistent between tabs
                    paddingTop: 27,
                }}
            >
                <div className="flex-grow mr-4">
                    <p className="text-sm text-tertiary-400">
                        {uploadsAvailableLabel}{' '}
                        <Link
                            className="text-sm underline text-primary-500 respond-to-mouse"
                            type="button"
                            to={({ search }) => ({
                                hash: HASHES.SETTINGS.PAYMENT_PLAN,
                                pathname: PATHS.SETTINGS,
                                search,
                                state: {
                                    fromLink: true,
                                },
                            })}
                        >
                            {
                                locale.enUS.MANAGE_DETECTIONS.DETECTION_SETTINGS
                                    .ENABLED_STATUS.UPDATE_PAYMENT_PLAN
                            }
                        </Link>
                    </p>
                </div>
            </div>
        </>
    );
};
